<script setup lang="ts">
import type { AccordionItem } from '#ui/types';
import lodashCountBy from 'lodash-es/countBy';
import lodashUniqBy from 'lodash-es/uniqBy';

import { FiveLevelStateOfUse, ThreeLevelStateOfUse } from '~/apiClient';

enum Rating {
    Excellent = 5,
    Good = 4,
    Fair = 3,
    Critical = 2,
    Poor = 1,
}

export type AssetSpecValue = string | number | boolean | null;

export type AssetSpecItem = [TranslationKey, AssetSpecValue];

export type AssetSpecsGroup = AssetSpecItem[];

export type AssetSpecsSection = AssetSpecsGroup[];

export type AssetStateOfUseLevel = ThreeLevelStateOfUse | FiveLevelStateOfUse | 'missing_part';

export type AssetStateOfUseItem = {
    description: string;
    level: AssetStateOfUseLevel;
};

export type SaleStateOfUse = AssetStateOfUseItem | AssetStateOfUseItem[] | undefined;

interface SdpSpecsSectionProps {
    title: string;
    defaultOpen?: boolean;
    groupedByAsset?: boolean;
    specs: AssetSpecsSection;
    stateOfUse?: SaleStateOfUse;
}

// PROPS
const { title, specs, stateOfUse } = defineProps<SdpSpecsSectionProps>();

// COMPOSABLES
const { tt } = useTypedI18n();

// DATA
const isNotEmpty = computed<boolean>(() => !!stateOfUse || specs.some(group => group.some(spec => spec[1] !== null)));
const adaptedStateOfUse = computed(() => {
    if (!stateOfUse) return undefined;

    if (!Array.isArray(stateOfUse)) return [{ ...stateOfUse, rating: getRating(stateOfUse?.level), count: undefined }];

    const counter = lodashCountBy(stateOfUse, item => item.level);

    return lodashUniqBy(stateOfUse, item => item.level)
        .map(item => ({ ...item, rating: getRating(item.level), count: counter[item.level] ?? 1 }))
        .toSorted((a, b) => (b.rating ?? 0) - (a.rating ?? 0));
});
const sectionStateOfUse = computed(() => {
    if (!stateOfUse || Array.isArray(stateOfUse)) return undefined;

    return { rating: getRating(stateOfUse.level), description: stateOfUse.description };
});
const accordionItems = computed<AccordionItem[]>(() => [{ label: title }]);

// METHODS
const getRating = (stateOfUse: AssetStateOfUseLevel): number | undefined => {
    switch (stateOfUse) {
        case ThreeLevelStateOfUse.Excellent:
        case FiveLevelStateOfUse.Excellent:
            return Rating.Excellent;
        case FiveLevelStateOfUse.Good:
            return Rating.Good;
        case FiveLevelStateOfUse.Fair:
        case ThreeLevelStateOfUse.Fair:
            return Rating.Fair;
        case FiveLevelStateOfUse.Poor:
            return Rating.Poor;
        case ThreeLevelStateOfUse.Critical:
        case FiveLevelStateOfUse.Critical:
            return Rating.Critical;
    }

    return undefined;
};
</script>

<template>
    <BaseAccordion v-if="isNotEmpty" size="xl" :items="accordionItems" :default-open="defaultOpen">
        <template v-if="sectionStateOfUse?.rating" #trailingIcon>
            <BaseTooltip :text="sectionStateOfUse.description">
                <UBadge class="whitespace-nowrap" trailing-icon="i-lucide-star" color="success" variant="outline">
                    {{ sectionStateOfUse.rating }} / {{ Rating.Excellent }}
                </UBadge>
            </BaseTooltip>
        </template>
        <template #content>
            <div v-if="stateOfUse" class="mb-3 flex flex-col items-stretch gap-2">
                <div v-for="(item, index) in adaptedStateOfUse" :key="index" class="flex flex-1 flex-col items-stretch gap-1">
                    <div v-if="!!item.count" class="flex items-center gap-1">
                        <UBadge v-if="item.rating" class="whitespace-nowrap" trailing-icon="i-lucide-star" color="success" variant="outline">
                            {{ item.rating }} / {{ Rating.Excellent }}
                        </UBadge>
                        <span>{{ tt('asset.vehicleCount', { count: item.count }) }}</span>
                    </div>
                    <span class="text-dimmed">{{ item.description }}</span>
                </div>
            </div>

            <div class="mb-3 flex flex-col gap-6 md:grid md:grid-cols-2">
                <template v-for="(group, i) in specs" :key="i">
                    <div v-if="group.some(spec => spec[1] !== null)" class="flex flex-col gap-0">
                        <h3 v-if="groupedByAsset" class="text-highlighted text-base leading-6 font-medium">
                            {{ tt('asset.vehicleIndex', { index: i + 1 }) }}
                        </h3>
                        <SdpSpecItem v-for="(spec, j) in group" :key="j" :name="tt(spec[0])" :value="spec[1]" />
                    </div>
                </template>
            </div>
        </template>
    </BaseAccordion>
</template>
